import { z } from 'zod';

import {
  hostnameSchema,
  ipv4Schema,
  isoDateTimeSchema,
  positiveIntSchema,
  uuidSchema,
} from '../common/primitives';

/**
 * Die Edge als Entität — bis M4a wusste der Master von ihr nichts.
 *
 * ## Was vorher fehlte
 *
 * Die Edge war eine Umgebungsvariable: `EDGE_CONFIG_TOKEN` als geteiltes
 * Geheimnis, `EDGE_ALLOWED_IPS` als Zugangsliste, `EDGE_PUBLIC_IPS` als die
 * Adressen, gegen die eine Kundendomain verifiziert wird. Eine zweite Edge
 * bedeutete damit: Master-Konfiguration ändern und den Master **neu starten** —
 * für einen Vorgang, der den Kundenverkehr nicht berühren sollte.
 *
 * Schlimmer war die Nebenwirkung: Dasselbe Geheimnis für alle Edges lässt sich
 * nicht einzeln zurückziehen. Eine kompromittierte Edge hätte die Rotation des
 * Tokens auf allen anderen erzwungen.
 *
 * ## Die Edge hat keinen Agent — und braucht keinen
 *
 * Ihr Lebenszeichen ist die Konfigurationsabfrage, die Traefik ohnehin alle
 * fünf Sekunden macht. Ein zusätzlicher Dienst, der meldet „ich lebe", während
 * daneben ein Traefik dasselbe beweist, wäre eine zweite Wahrheit über
 * denselben Sachverhalt. Deshalb schreibt der Wächter der Konfigurations-Route
 * `lastSeenAt` fort, und daraus leitet sich der Status ab.
 *
 * Die Folge ist bewusst: Ein Status `offline` heißt hier **nicht**, dass die
 * Kundenseiten nicht erreichbar sind. Traefik behält seine zuletzt geholte
 * Konfiguration und liefert weiter aus. Es heißt: Diese Edge erfährt von
 * Änderungen nichts mehr.
 */

/**
 * Pfad, unter dem sich eine Edge registriert — relativ zu `INTERNAL_API_PREFIX`.
 *
 * Bewusst neben `edge/config` und nicht unter `agent/v1`: Der Aufrufer ist kein
 * Agent, sondern ein Shell-Skript mit `curl`, und der Endpunkt ist als
 * einziger in diesem Bereich **nicht** durch den Edge-Token geschützt — er ist
 * ja der Weg, an ihn zu kommen. Der Bootstrap-Token tritt an seine Stelle.
 */
export const EDGE_ENROLL_PATH = 'edge/enroll';

export const EDGE_STATUS = {
  /** Angelegt, Bootstrap-Token ausgegeben, aber noch nie registriert. */
  enrolling: 'enrolling',
  /** Holt ihre Konfiguration regelmäßig ab. */
  online: 'online',
  /** Abfrage überfällig, aber noch nicht als ausgefallen gewertet. */
  degraded: 'degraded',
  /** Abfrage lange überfällig. Diese Edge erfährt von Änderungen nichts mehr. */
  offline: 'offline',
} as const;

export type EdgeStatus = (typeof EDGE_STATUS)[keyof typeof EDGE_STATUS];

export const edgeStatusSchema = z.enum([
  EDGE_STATUS.enrolling,
  EDGE_STATUS.online,
  EDGE_STATUS.degraded,
  EDGE_STATUS.offline,
]);

/**
 * Laufende Nummer einer Edge.
 *
 * Sie ist keine Zierde: Aus ihr entsteht der Name der Datenbank-Oberfläche
 * (`dbms-<n>.<hauptdomain>`), und der steht in einem Zertifikat und in einem
 * DNS-Eintrag. Deshalb wird sie beim Anlegen vergeben und danach nie geändert.
 */
export const edgeOrdinalSchema = positiveIntSchema.max(99, {
  message: 'Mehr als 99 Edges sind nicht vorgesehen.',
});

/**
 * Lesesicht auf eine Edge.
 *
 * Wie bei `nodeViewSchema` ein eigener Vertrag statt einer Serialisierung der
 * Entität: Dort liegen Token-Hashes, und die gehen niemals über die API.
 */
export const edgeViewSchema = z.object({
  id: uuidSchema,
  ordinal: edgeOrdinalSchema,
  /** Freie Notiz des Betreibers, z. B. "Hetzner FSN1, seit 08/2026". */
  note: z.string().max(200).nullable(),
  /** `null` bis zur Registrierung — die Edge meldet ihn selbst. */
  hostname: hostnameSchema.nullable(),
  /** Die Adresse, auf die Kundendomains zeigen müssen. `null` bis zur Registrierung. */
  publicIpv4: ipv4Schema.nullable(),
  /** Über diese Adresse spricht die Edge mit den Workern. `null` bis zur Registrierung. */
  privateIpv4: ipv4Schema.nullable(),
  /** Der Name, unter dem die Datenbank-Oberfläche dieser Edge erreichbar ist. */
  dbmsHostname: hostnameSchema,
  status: edgeStatusSchema,
  /** Begründung des Status, auf Deutsch und direkt anzeigbar. */
  statusReason: z.string().nullable(),
  enrolledAt: isoDateTimeSchema.nullable(),
  /** Zeitpunkt der letzten Konfigurationsabfrage. Das Lebenszeichen der Edge. */
  lastSeenAt: isoDateTimeSchema.nullable(),
  /** Alter der letzten Abfrage in Millisekunden — erspart dem Client die Rechnung. */
  lastSeenAgeMs: z.number().nonnegative().nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export type EdgeView = z.infer<typeof edgeViewSchema>;

/**
 * Registrierung einer Edge.
 *
 * Gerufen vom Bootstrap-Skript mit `curl`, nicht von einem Agent. Deshalb so
 * schmal wie möglich: Was hier steht, muss ein Shell-Skript aus `ip` und
 * `hostname` zusammensetzen können.
 */
export const edgeEnrollmentRequestSchema = z.object({
  bootstrapToken: z
    .string()
    .min(32, { message: 'Bootstrap-Token ist zu kurz — vermutlich unvollständig kopiert.' })
    .max(512),
  hostname: hostnameSchema,
  /** Die öffentliche Adresse. Kundendomains werden künftig gegen sie verifiziert. */
  publicIpv4: ipv4Schema,
  /** Die Adresse im privaten Netz. Über sie erreicht die Edge die Worker. */
  privateIpv4: ipv4Schema,
});

export type EdgeEnrollmentRequest = z.infer<typeof edgeEnrollmentRequestSchema>;

export const edgeEnrollmentResponseSchema = z.object({
  edgeId: uuidSchema,
  ordinal: edgeOrdinalSchema,
  /**
   * Der Dauer-Token dieser Edge. Nur hier im Klartext.
   *
   * Er ersetzt `EDGE_CONFIG_TOKEN` für diese eine Edge und lässt sich einzeln
   * zurückziehen, indem die Zeile entfernt wird.
   */
  configToken: z.string().min(32),
  /** Der Name, unter dem die Datenbank-Oberfläche dieser Edge laufen soll. */
  dbmsHostname: hostnameSchema,
  enrolledAt: isoDateTimeSchema,
});

export type EdgeEnrollmentResponse = z.infer<typeof edgeEnrollmentResponseSchema>;
